import React, { useEffect, useMemo, useRef, useState, Fragment } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import DataTable from "datatables.net-dt";
import "datatables.net-dt/css/dataTables.dataTables.css";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";

/* ----------------- Metrics ----------------- */
const METRICS = [
  { key: "ALL", label: "All metrics" },
  { key: "O2", label: "O₂ (%)" },
  { key: "CO", label: "CO (ppm)" },
  { key: "SO2", label: "SO₂ (ppm)" },
  { key: "NOX", label: "NOx (ppm)" },
  { key: "PARTICULATE", label: "Particulate" },
  { key: "FLOWRATE", label: "Flowrate" },
  { key: "OPACITY", label: "Opacity" },
  { key: "TEMPERATURE", label: "Temperature (°C)" },
  { key: "PRESSURE", label: "Pressure" },
];

/* ----------------- Helpers ----------------- */
const SHAPE = {
  O2: { base: 20.5, amp: 1.2 },
  CO: { base: 320, amp: 90 },
  SO2: { base: 40, amp: 12 },
  NOX: { base: 110, amp: 25 },
  PARTICULATE: { base: 5, amp: 2 },
  FLOWRATE: { base: 30, amp: 6 },
  OPACITY: { base: 2, amp: 0.8 },
  TEMPERATURE: { base: 150, amp: 18 },
  PRESSURE: { base: 1.05, amp: 0.15 },
};

function valueFor(key, ts) {
  const shape = SHAPE[key];
  if (!shape) return 0;
  const seed = key.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const v = shape.base + shape.amp * (0.5 + 0.5 * Math.sin(ts / 3.6e6 + seed));
  return Number(Math.max(0, v).toFixed(2));
}

function toLocalInputValue(ms) {
  const d = new Date(ms);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

function pickStepMinutes(msRange) {
  const h = msRange / 3.6e6;
  if (h <= 24) return 15;
  if (h <= 72) return 30;
  if (h <= 168) return 60;
  return 180;
}

function generateTimepoints(startMs, endMs) {
  const stepMin = pickStepMinutes(endMs - startMs);
  const step = stepMin * 60 * 1000;
  const pts = [];
  for (let t = startMs; t <= endMs; t += step) pts.push(t);
  return pts;
}

/* ---------- Fancy Tailwind Dropdown (Headless UI) ---------- */
function FancySelect({ value, onChange, options, className = "" }) {
  const selected =
    options.find((o) => o.value === value) || options[0] || { label: "", value: "" };
  return (
    <Listbox value={value} onChange={onChange}>
      <div className={`relative ${className}`}>
        <Listbox.Button
          className="relative w-full h-10 flex items-center
                     rounded-md border border-gray-300 bg-white pl-3 pr-10
                     text-left text-sm text-gray-800 shadow-sm
                     hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <span className="block truncate leading-none">{selected.label}</span>
          <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-500" />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
          enter="transition ease-out duration-150"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
        >
          <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
            {options.map((opt) => (
              <Listbox.Option
                key={opt.value}
                value={opt.value}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 pl-9 pr-3 ${
                    active ? "bg-blue-50 text-blue-700" : "text-gray-800"
                  }`
                }
              >
                {({ selected: isSel }) => (
                  <>
                    {isSel ? (
                      <span className="absolute inset-y-0 left-2 flex items-center text-blue-600">
                        <CheckIcon className="h-5 w-5" />
                      </span>
                    ) : null}
                    <span className={`block truncate ${isSel ? "font-medium" : "font-normal"}`}>
                      {opt.label}
                    </span>
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

/* ----------------- Page ----------------- */
export default function HistoricalData() {
  // UI (draft) state — does NOT change results until "Apply"
  const now = Date.now();
  const [uiMetric, setUiMetric] = useState("ALL");
  const [uiFrom, setUiFrom] = useState(now - 24 * 3600 * 1000);
  const [uiTo, setUiTo] = useState(now);
  const [uiShowChart, setUiShowChart] = useState(true);
  const [uiShowTable, setUiShowTable] = useState(true);

  const invalidRange = uiTo <= uiFrom;

  // Applied config (what the page actually renders)
  const [applied, setApplied] = useState(null); // null until first Apply

  const doApply = () => {
    if (invalidRange) return;
    setApplied({
      metric: uiMetric,
      from: uiFrom,
      to: uiTo,
      showChart: uiShowChart,
      showTable: uiShowTable,
    });
  };

  const hasApplied = !!applied;

  // Derived from applied config
  const timepoints = useMemo(() => {
    if (!applied || applied.to <= applied.from) return [];
    return generateTimepoints(applied.from, applied.to);
  }, [applied]);

  const appliedMetric = applied?.metric ?? "ALL";
  const activeMetrics = useMemo(
    () => METRICS.filter((m) => m.key !== "ALL" && (appliedMetric === "ALL" || m.key === appliedMetric)),
    [appliedMetric]
  );

  /* ---------- Chart ---------- */
  const series =
    appliedMetric === "ALL"
      ? METRICS.filter((m) => m.key !== "ALL").map((m) => ({
          name: m.label,
          data: timepoints.map((t) => [t, valueFor(m.key, t)]),
          marker: { enabled: true, radius: 2 },
          lineWidth: 2,
        }))
      : [
          {
            name: METRICS.find((m) => m.key === appliedMetric)?.label || appliedMetric,
            data: timepoints.map((t) => [t, valueFor(appliedMetric, t)]),
            marker: { enabled: true, radius: 3 },
            lineWidth: 3,
          },
        ];

  const chartOptions = useMemo(
    () => ({
      chart: { type: "spline", height: 460, spacing: [8, 16, 16, 16] },
      title: { text: "Historical Data" },
      subtitle: { text: hasApplied ? null : "Pick options and click Apply" },
      time: { useUTC: false },
      xAxis: {
        type: "datetime",
        min: timepoints[0],
        max: timepoints[timepoints.length - 1],
        tickInterval: 24 * 3600 * 1000,
        title: { text: "Date" },
      },
      yAxis: {
        title: {
          text:
            appliedMetric === "ALL"
              ? "Value"
              : METRICS.find((m) => m.key === appliedMetric)?.label || "Value",
        },
      },
      tooltip: {
        xDateFormat: "%A, %e %b %Y, %H:%M",
        shared: appliedMetric === "ALL",
        pointFormat:
          appliedMetric === "ALL"
            ? '<span style="font-weight:600">{series.name}</span>: <b>{point.y:.2f}</b><br/>'
            : "<b>{point.y:.2f}</b>",
      },
      legend: { enabled: hasApplied && applied?.showChart },
      credits: { enabled: false },
      exporting: { enabled: false },
      series,
    }),
    [hasApplied, applied?.showChart, timepoints, appliedMetric, series]
  );

  /* ---------- DataTables ---------- */
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  useEffect(() => {
    if (!applied?.showTable || !tableRef.current) {
      // If table is hidden, be sure to clean up any previous instance
      if (dtRef.current) {
        try { dtRef.current.destroy(); } catch {}
        dtRef.current = null;
      }
      if (tableRef.current) tableRef.current.innerHTML = "";
      return;
    }

    const columns = [
      { title: "Timestamp", data: "TIMESTAMP" },
      { title: "Epoch (ms)", data: "TIMESTAMP_EPOCH" },
      ...activeMetrics.map((m) => ({ title: m.label, data: m.key })),
    ];

    const data = timepoints.map((ts) => {
      const row = { TIMESTAMP: new Date(ts).toLocaleString(), TIMESTAMP_EPOCH: ts };
      activeMetrics.forEach((m) => (row[m.key] = valueFor(m.key, ts)));
      return row;
    });

    if (dtRef.current) {
      try { dtRef.current.destroy(); } catch {}
      dtRef.current = null;
    }
    tableRef.current.innerHTML = "";

    dtRef.current = new DataTable(tableRef.current, {
      data,
      columns,
      paging: true,
      pageLength: 25,
      responsive: true,
      ordering: true,
      order: [[1, "desc"]],
      searching: true,
      info: true,
      deferRender: true,
    });

    return () => {
      if (dtRef.current) {
        try { dtRef.current.destroy(); } catch {}
        dtRef.current = null;
      }
      if (tableRef.current) tableRef.current.innerHTML = "";
    };
  }, [applied?.showTable, timepoints, activeMetrics]);

  /* ---------- CSV Export ---------- */
  const exportCSV = () => {
    if (!hasApplied || timepoints.length === 0) return;
    const cols = ["Timestamp", "Epoch (ms)", ...activeMetrics.map((m) => m.label)];
    const header = cols.join(",");
    const lines = timepoints.map((ts) => {
      const base = [new Date(ts).toLocaleString(), ts];
      const vals = activeMetrics.map((m) => valueFor(m.key, ts));
      return [...base, ...vals].map((v, i) => (i === 0 ? `"${v}"` : String(v))).join(",");
    });
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rd4_historical_${appliedMetric === "ALL" ? "all" : appliedMetric}_${new Date(
      applied.from
    )
      .toISOString()
      .slice(0, 10)}_${new Date(applied.to).toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /* ---------- UI ---------- */
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Title */}
        <h2 className="text-3xl font-semibold text-gray-800">Historical Data</h2>

        {/* Toolbar (carded) */}
        <div className="bg-white rounded-lg shadow p-4">
          {/* Row 1: inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
            {/* Metric (draft) */}
            <div className="lg:col-span-3">
              <label className="mb-1 block text-sm font-medium text-gray-600">Metric</label>
              <FancySelect
                value={uiMetric}
                onChange={setUiMetric}
                options={METRICS.map((m) => ({ value: m.key, label: m.label }))}
                className="w-full"
              />
            </div>

            {/* From (draft) */}
            <div className="lg:col-span-3">
              <label className="mb-1 block text-sm font-medium text-gray-600">From</label>
              <input
                type="datetime-local"
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm shadow-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={toLocalInputValue(uiFrom)}
                onChange={(e) => setUiFrom(new Date(e.target.value).getTime())}
              />
            </div>

            {/* To (draft) */}
            <div className="lg:col-span-3">
              <label className="mb-1 block text-sm font-medium text-gray-600">To</label>
              <input
                type="datetime-local"
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm shadow-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={toLocalInputValue(uiTo)}
                onChange={(e) => setUiTo(new Date(e.target.value).getTime())}
              />
            </div>

            {/* Outputs checkboxes */}
            <div className="lg:col-span-3">
              <label className="mb-1 block text-sm font-medium text-gray-600">Outputs</label>
              <div className="flex items-center gap-4 h-10">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={uiShowChart}
                    onChange={(e) => setUiShowChart(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Chart</span>
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={uiShowTable}
                    onChange={(e) => setUiShowTable(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Table</span>
                </label>
              </div>
            </div>

            {/* Row 2: actions */}
            <div className="lg:col-span-12 flex justify-start gap-3 pt-1">
              <button
                onClick={doApply}
                disabled={invalidRange}
                className="inline-flex h-10 items-center rounded-md bg-blue-600 px-3 text-sm
                           font-medium text-white shadow-sm hover:bg-blue-700
                           focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
              <button
                onClick={exportCSV}
                disabled={!hasApplied || timepoints.length === 0}
                className="inline-flex h-10 items-center rounded-md border border-gray-300 bg-white px-3 text-sm
                           font-medium text-gray-700 shadow-sm hover:bg-gray-50
                           focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Export CSV
              </button>
            </div>

            {invalidRange && (
              <div className="lg:col-span-12 text-sm text-red-600">
                The <strong>To</strong> time must be after the <strong>From</strong> time.
              </div>
            )}
          </div>
        </div>

        {/* States & Results */}
        {!hasApplied ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-gray-600">
            Pick options above, then hit <span className="font-medium">Apply</span>.
          </div>
        ) : timepoints.length === 0 ? (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800">
            No points in the selected range.
          </div>
        ) : !applied.showChart && !applied.showTable ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-gray-600">
            No outputs selected — check <span className="font-medium">Chart</span> and/or{" "}
            <span className="font-medium">Table</span> above and click Apply.
          </div>
        ) : (
          <>
            {applied.showChart && (
              <div className="bg-white rounded-lg shadow p-4">
                <HighchartsReact highcharts={Highcharts} options={chartOptions} />
              </div>
            )}

            {applied.showTable && (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="mb-3 text-lg font-medium text-gray-800">
                  Results — {appliedMetric === "ALL" ? "All metrics" : METRICS.find((m) => m.key === appliedMetric)?.label}
                </div>
                <div className="overflow-auto">
                  <table ref={tableRef} className="display w-full text-sm" />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
